import { useState } from 'react';
import { Download, Copy, Share2, Check } from 'lucide-react';
import { logError, logWarn } from '../lib/logger';

export const ShareActions = ({ story, shareUrl, shareText, previewUrl, blob, generating, onDownload, onShared }) => {
  const [linkCopied, setLinkCopied] = useState(false);
  const [imgCopied, setImgCopied] = useState(false);
  const [imgFallback, setImgFallback] = useState(false);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setLinkCopied(true);
      onShared();
      setTimeout(() => setLinkCopied(false), 1800);
    } catch (e) {
      const ta = document.createElement('textarea');
      ta.value = shareUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setLinkCopied(true);
      onShared();
      setTimeout(() => setLinkCopied(false), 1800);
    }
  };

  const handleCopyImage = async () => {
    if (!blob) return;
    try {
      // eslint-disable-next-line no-undef
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      setImgCopied(true);
      onShared();
      setTimeout(() => setImgCopied(false), 1800);
    } catch (e) {
      logWarn('ShareActions:copyImage', 'clipboard image write unsupported, falling back to download');
      onDownload();
      setImgFallback(true);
      setTimeout(() => setImgFallback(false), 2200);
    }
  };

  const handleNativeShare = async () => {
    if (!blob) return;
    try {
      const file = new File([blob], `clarity-${story.id}.png`, { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: story.primary_headline, text: shareText, url: shareUrl });
        onShared();
      } else if (navigator.share) {
        await navigator.share({ title: story.primary_headline, text: shareText, url: shareUrl });
        onShared();
      } else {
        handleCopyLink();
      }
    } catch (e) {
      if (e?.name !== 'AbortError') logError('ShareActions:nativeShare', e);
    }
  };

  const canNativeShare = typeof navigator !== 'undefined' && !!navigator.share;

  return (
    <div className="p-4 sm:p-5 grid grid-cols-2 gap-2 sm:gap-3">
      {canNativeShare && (
        <button
          onClick={handleNativeShare}
          disabled={generating}
          className="col-span-2 flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="share-native-button"
        >
          <Share2 className="w-4 h-4" />
          Share…
        </button>
      )}
      <button
        onClick={handleCopyImage}
        disabled={generating || !blob}
        className="flex items-center justify-center gap-2 px-4 py-3 border border-gray-200 hover:bg-gray-50 rounded-lg text-sm font-medium text-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="share-copy-image-button"
      >
        {(imgCopied || imgFallback) ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
        {imgCopied ? 'Copied' : imgFallback ? 'Saved as PNG' : 'Copy image'}
      </button>
      <button
        onClick={onDownload}
        disabled={generating || !previewUrl}
        className="flex items-center justify-center gap-2 px-4 py-3 border border-gray-200 hover:bg-gray-50 rounded-lg text-sm font-medium text-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="share-download-button"
      >
        <Download className="w-4 h-4" />
        Download
      </button>
      <button
        onClick={handleCopyLink}
        className="col-span-2 flex items-center justify-center gap-2 px-4 py-3 border border-gray-200 hover:bg-gray-50 rounded-lg text-sm font-medium text-gray-700 transition-colors"
        data-testid="share-copy-link-button"
      >
        {linkCopied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
        {linkCopied ? 'Link copied' : 'Copy link'}
      </button>
    </div>
  );
};
